/* Vista Tenant Home — wide, cinematic, magazine-cover, outdoor.

   Emotional intent: SPACIOUSNESS. Built for landscape, travel, adventure.
   Hero is a 21:9 ULTRA-WIDE letterboxed photo (NOT 100vh). Tiny magazine
   stamps overlaid: "ISSUE №07", coordinates "40°43'N 74°00'W", date.
   Vertical type strips on the sides ("EXPLORE → BOOK"). All caps
   sparingly used. Lots of tracking. Sections feel like magazine spreads.

   Structurally distinct: letterboxed (not full-bleed), itinerary-style
   service cards with route numbers, "FROM THE FIELD" about treatment. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { optimizeImageUrl, optimizedSrcSet } from "@/lib/imageOptim";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

const FALLBACK = "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=2400&q=80";

export default function VistaTenantHome() {
  const { settings, loading } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const heroImg = settings?.hero_image || settings?.portfolio_images?.[0] || FALLBACK;
  const heroPos = `${settings?.hero_focal_x ?? 50}% ${settings?.hero_focal_y ?? 50}%`;
  const headline = settings?.hero_title_line1 || "Far enough.";
  const subhead = settings?.hero_title_line2 || "Out there.";
  const intro = settings?.hero_description || `${studioName} photographs landscape, travel, and adventure — out where the cell signal drops and the light starts to behave.`;
  const aboutTitle = settings?.about_title || "From the field.";
  const aboutBody = settings?.about_body || "We work outside. Coastlines at 5am, alpine huts in October, deserts that look like another planet. Bring your boots, your patience, and your second SD card.";
  const location = settings?.contact_location || "New Jersey · Will travel";
  const port = (settings?.portfolio_images || []).slice(0, 5);

  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(Array.isArray(r.data) ? r.data.slice(0, 3) : [])).catch(() => {});
  }, []);
  useEffect(() => { document.title = `${studioName} — Landscape & Adventure`; }, [studioName]);

  if (loading && !settings) return <EditorialSkeleton eyebrow="Loading the route." />;

  // Template-scoped stamps (Vista's magazine cover chrome). Tenants edit
  // these in Site Settings → Template-specific so the cover doesn't have
  // to lie about "Issue №06 · 40°43'N · 74°00'W" forever.
  const tf = settings?.template_fields?.vista || {};
  const issueNo = (tf.issue_number || String(new Date().getMonth() + 1).padStart(2, "0")).toString();
  const coordinates = (tf.coordinates || "40°43′N · 74°00′W").toString();

  return (
    <div data-testid="vista-tenant-home"
      style={{ background: "var(--tenant-bg, #F8F9FB)", color: "var(--tenant-fg, #102A33)" }}>

      {/* ── HERO — letterboxed 21:9, magazine-stamps overlaid ────────── */}
      <section className="px-6 lg:px-12 pt-10 pb-12" data-testid="vista-hero">
        <div className="max-w-[1480px] mx-auto">
          <div className="relative overflow-hidden" style={{ aspectRatio: "21/9" }}>
            <img src={optimizeImageUrl(heroImg, { w: 2400 })}
              srcSet={optimizedSrcSet(heroImg, [1200, 1800, 2400]) || undefined}
              sizes="100vw"
              alt={`${studioName} cover plate`}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: heroPos }}
              loading="eager" fetchPriority="high" decoding="async" />
            {/* Cover-stamp overlays */}
            <div className="absolute top-4 left-4 right-4 flex justify-between text-[9px] uppercase tracking-[0.42em] text-white/90 mix-blend-difference">
              <span>Issue №{issueNo} · {new Date().getFullYear()}</span>
              <span>{location}</span>
            </div>
            <div className="absolute bottom-4 left-4 right-4 flex justify-between text-[9px] uppercase tracking-[0.42em] text-white/90 mix-blend-difference">
              <span>{coordinates}</span>
              <span>{studioName.toUpperCase()}</span>
            </div>
          </div>
          {/* Headline below — magazine-style */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-12">
            <div className="md:col-span-2 md:border-r md:pr-4" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }}>
              <div className="text-[9px] uppercase tracking-[0.42em] mb-2" style={{ color: "var(--tenant-accent)" }}>Cover Story</div>
              <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-fg-soft)" }}>p. 01 / {studioName}</div>
            </div>
            <div className="md:col-span-10">
              <h1 className="leading-[0.95]" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.75rem, 7vw, 6rem)" }}>
                <span>{headline} </span>
                <span style={{ color: "var(--tenant-accent)" }} className="italic">{subhead}</span>
              </h1>
              <p className="mt-8 max-w-2xl text-base leading-relaxed" style={{ color: "var(--tenant-fg-soft)" }}>
                {intro}
              </p>
              <div className="mt-10 flex items-center gap-6">
                <Link to={tenantHref("/booking")} data-testid="vista-hero-book"
                  className="inline-flex items-center gap-3 px-6 py-3 text-[10px] uppercase tracking-[0.32em] border-2 transition-colors"
                  style={{ borderColor: "var(--tenant-accent)", color: "var(--tenant-accent)" }}>
                  Plan the route →
                </Link>
                <Link to={tenantHref("/portfolio")} className="text-[10px] uppercase tracking-[0.32em] border-b pb-0.5"
                      style={{ color: "var(--tenant-fg-soft)", borderColor: "var(--tenant-fg-soft)" }}>
                  Past expeditions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FROM THE FIELD — about, with location stamp ──────────────── */}
      <section className="px-6 lg:px-12 py-20 border-t" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }}>
        <div className="max-w-[1480px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-3">
            <div className="text-[9px] uppercase tracking-[0.42em] mb-3" style={{ color: "var(--tenant-accent)" }}>From the Field</div>
            <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-fg-soft)" }}>
              {location}<br />Year-round
            </div>
          </div>
          <div className="md:col-span-9">
            <h2 className="leading-tight" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
              {aboutTitle}
            </h2>
            <p className="mt-6 text-base leading-relaxed max-w-2xl" style={{ color: "var(--tenant-fg-soft)" }}>
              {aboutBody}
            </p>
          </div>
        </div>
      </section>

      {/* ── EXPEDITIONS — services as itinerary cards ────────────────── */}
      {services.length > 0 && (
        <section className="px-6 lg:px-12 py-20 border-t" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }} data-testid="vista-services">
          <div className="max-w-[1480px] mx-auto">
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="text-[9px] uppercase tracking-[0.42em] mb-3" style={{ color: "var(--tenant-accent)" }}>Expeditions</div>
                <h2 className="leading-tight" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
                  Routes we run.
                </h2>
              </div>
              <Link to={tenantHref("/services")} className="text-[10px] uppercase tracking-[0.32em] border-b pb-0.5"
                    style={{ color: "var(--tenant-accent)", borderColor: "var(--tenant-accent)" }}>
                All routes →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {services.map((svc, i) => (
                <Link key={svc.id} to={tenantHref(`/services#${svc.id || svc.slug || ""}`)}
                  className="block border-2 p-6 transition-colors hover:bg-[color:var(--tenant-accent)]/5"
                  style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
                      Route №{String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-fg-soft)" }}>
                      {svc.duration_minutes ? `${Math.round(svc.duration_minutes / 60)}H` : "FULL DAY"}
                    </div>
                  </div>
                  <h3 className="text-xl leading-tight mb-2" style={{ fontFamily: "var(--tenant-serif)" }}>{svc.name}</h3>
                  {svc.short_description && (
                    <p className="text-sm leading-relaxed" style={{ color: "var(--tenant-fg-soft)" }}>{svc.short_description}</p>
                  )}
                  <div className="mt-6 text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-accent)" }}>
                    Begin route →
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── HORIZONTAL PORTFOLIO STRIP ───────────────────────────────── */}
      {port.length > 0 && (
        <section className="py-20 border-t" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }}>
          <div className="px-6 lg:px-12 max-w-[1480px] mx-auto flex items-end justify-between mb-10">
            <div>
              <div className="text-[9px] uppercase tracking-[0.42em] mb-3" style={{ color: "var(--tenant-accent)" }}>Plates</div>
              <h2 className="leading-tight" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}>
                Recent frames.
              </h2>
            </div>
          </div>
          <div className="overflow-x-auto pb-4 px-6 lg:px-12">
            <div className="flex gap-6 min-w-min">
              {port.map((src, i) => (
                <img key={i} src={optimizeImageUrl(src, { w: 900 })} alt=""
                  className="h-[420px] w-auto object-cover shrink-0"
                  loading="lazy" decoding="async" />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
